import classNames from 'classnames';
import $ from 'jquery';
import * as PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import getTestProps from 'test/lib/cypress/getTestProps';
import {WINDOW_PADDING} from 'ui-library/menus/Menu';
import {isInBoundsOfScrollContainers} from 'util/findScrollContainer';
import throttle from 'util/throttle';
import {ElementMouseEvent} from 'util/Types';

export interface AbsoluteDropdownContainerProps {
  children: JSX.Element;
  className?: string;
  open: boolean;
  onRequestClose: () => void;
  menu: JSX.Element | null;
  shouldCloseOnInsideClick?: boolean;
  /**
   * If set to true, the dropdown will close if the user clicks outside of the dropdown menu.
   * This is only active when using the noBackdrop prop.  If noBackdrop is not set, the dropdown
   * always closes when the backdrop is clicked
   */
  shouldCloseOnOutsideClick?: boolean;
  canDropUnder?: boolean;
  alwaysDropDown?: boolean;
  alwaysDropUp?: boolean;
  wrapMenuWithContext?: (menu: JSX.Element | null) => JSX.Element | null;
  /**
   * If set to true, the user will be able to click to interact with any element on the page. Otherwise, the
   * user can click anywhere outside of the menu to close the menu without accidentally interacting with an
   * element on the page.
   */
  noBackdrop?: boolean;
}

/**
 * Component that wraps a button to render a dropdown on body.
 * AbsoluteDropdownContainer takes care of:
 *  - positioning a backdrop behind the dropdown that closes the dropdown on mousedown
 *  - rendering the dropdown on body as if it were a child of the wrapped button
 *  - rendering the dropdown "up" if necessary
 */
class _AbsoluteDropdownContainer extends React.Component<AbsoluteDropdownContainerProps> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    open: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    menu: (props: AbsoluteDropdownContainerProps, ...args: [string, string, string, string]) => {
      if (props.open) {
        return PropTypes.node.isRequired(props, ...args);
      }
      return null;
    },
    wrapMenuWithContext: PropTypes.func,
    shouldCloseOnInsideClick: PropTypes.bool.isRequired,
    shouldCloseOnOutsideClick: PropTypes.bool,
    canDropUnder: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    shouldCloseOnInsideClick: false,
    shouldCloseOnOutsideClick: false,
    canDropUnder: false,
  };

  private _isMounted = false;

  private mount: HTMLDivElement | null = null;

  componentDidMount(): void {
    this._isMounted = true;
    this.handleOpenChange(false, this.props.open);
    window.addEventListener('scroll', this._handleBackdroplessScroll, true);
    window.addEventListener('mousedown', this._handleBackdroplessMouseDown);
  }

  componentDidUpdate(prevProps: AbsoluteDropdownContainerProps): void {
    this.handleOpenChange(prevProps.open, this.props.open);
    if (prevProps.open === this.props.open && this.props.open === true) {
      // Re-render the menu if it remains open
      this.renderAndPositionMenu();
    }
  }

  componentWillUnmount(): void {
    this._isMounted = false;
    window.removeEventListener('scroll', this._handleBackdroplessScroll, true);
    window.removeEventListener('mousedown', this._handleBackdroplessMouseDown);
    this.handleOpenChange(this.props.open, false);
  }

  _handleBackdroplessScroll = throttle(() => {
    if (!this._isMounted || !this.props.noBackdrop) {
      return;
    }
    // If the backdrop is not enabled, the user can scroll the page, so update the menu's fixed position.
    if (this.props.open) {
      this.renderAndPositionMenu();
    }
  }, 10);

  _handleBackdroplessMouseDown = (e: MouseEvent): void => {
    if (!this.props.noBackdrop) {
      return;
    }
    // If the user clicked anywhere that isn't the menu (i.e. what would have been the "backdrop" if
    // noBackdrop had not been specified), trigger the close behavior.
    if (this.props.open) {
      const dropdownButton = ReactDOM.findDOMNode(this) as Element;
      const menuElement = ReactDOM.findDOMNode(this.mount);
      if (
        (e.target &&
          this.props.shouldCloseOnOutsideClick &&
          menuElement &&
          !menuElement.contains(e.target as Element)) ||
        (dropdownButton && dropdownButton.contains(e.target as Element))
      ) {
        this.props.onRequestClose();
      }
    }
  };

  handleOpenChange(oldOpen: boolean, newOpen: boolean): void {
    if (oldOpen === newOpen) {
      return;
    }
    if (newOpen) {
      // Render the menu into the DOM, attached to body.
      this.mount = document.createElement('div');
      this.renderAndPositionMenu();
      document.body.appendChild(this.mount);
      // Register throttled resize handler to maintain menu's position
      window.addEventListener('resize', this.resizeHandler);
    } else {
      window.removeEventListener('resize', this.resizeHandler);
      if (this.mount) {
        document.body.removeChild(this.mount);
        ReactDOM.unmountComponentAtNode(this.mount);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mouseDownHandler = (e: ElementMouseEvent) => {
    // Close the window if the user interact with the backdrop.
    if ($(e.target).hasClass('backdrop')) {
      this.props.onRequestClose();
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  clickHandler = (e: ElementMouseEvent) => {
    if (this.props.shouldCloseOnInsideClick) {
      this.props.onRequestClose();
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  resize = () => {
    if (!this._isMounted || !this.props.open) {
      return;
    }
    this.renderAndPositionMenu();
  };

  resizeHandler = throttle(this.resize, 10);

  /**
   * Return whether or not the menu should be hidden despite {@link AbsoluteDropdownContainerProps.open}
   *
   * If no backdrop is being rendered, the user is able to scroll the page, which can cause the dropdown
   * target to go out view. Because we render the menu with a high z-index, this can result in a spooky
   * behavior where the menu popover continues to track the dropdown target even after its been scrolled out
   * of the view port.
   */
  private shouldHideMenu(element: Element): boolean {
    if (!this.props.noBackdrop) {
      return false;
    }
    return !isInBoundsOfScrollContainers(element);
  }

  renderAndPositionMenu(): void {
    const element = ReactDOM.findDOMNode(this) as Element;
    const shouldHide = this.shouldHideMenu(element);
    const rect = element.getBoundingClientRect();
    const dropup =
      (!this.props.alwaysDropDown && rect.top > $(window).height() / 2) || this.props.alwaysDropUp;
    const dropUnder = !dropup && this.props.canDropUnder;
    // Calculate a maxHeight to prevent the menu from extending beyond the screen.
    const maxHeight = (dropup ? rect.top : $(window).height() - rect.bottom) - WINDOW_PADDING;
    // Merge in the new `style` prop to the menu. Note: `menu` must thread `style` through to an HTML element
    // for the style to render.
    const menu =
      this.props.menu &&
      React.cloneElement(this.props.menu, {
        style: {...this.props.menu.props.style, maxHeight},
        dropup,
      });
    // Wrap menu with context after cloning, so style prop is on the correct element.
    const menuWithContext = this.props.wrapMenuWithContext ? this.props.wrapMenuWithContext(menu) : menu;
    const component = (
      <div
        className={classNames('absoluteDropdownContainer', {
          'absoluteDropdownContainer--noBackdrop': this.props.noBackdrop,
        })}
        style={{
          position: 'fixed',
          left: rect.left,
          top: dropUnder ? rect.bottom : rect.top,
          display: shouldHide ? 'none' : 'block',
        }}
        onMouseDown={this.mouseDownHandler}
        onClick={this.clickHandler}
        {...getTestProps({component: 'AbsoluteDropdownContainer', element: 'root'})}
      >
        {!this.props.noBackdrop && (
          <div
            className="backdrop"
            {...getTestProps({component: 'AbsoluteDropdownContainer', element: 'backdrop'})}
          />
        )}
        <div
          // Matches the size of DOM node for `@props.children` exactly, so menu is positioned correctly
          style={{width: rect.width, height: rect.height}}
          className={classNames('dropdown open is-open', this.props.className, {
            'drop-up': dropup, // menu drops up if the menu is more than halfway down
          })}
        >
          {menuWithContext}
        </div>
      </div>
    );
    renderToDOM(component, this.mount!);
  }

  render(): JSX.Element {
    return this.props.children;
  }
}

/**
 * Allow reassigning AbsoluteDropdownContainer in test to MockAbsoluteDropdownContainer by using mock().
 * Must cleanup with restore() afterwards.
 */
let AbsoluteDropdownContainer = _AbsoluteDropdownContainer; // eslint-disable-line import/no-mutable-exports

// eslint-disable-next-line no-restricted-exports
export {AbsoluteDropdownContainer as default};

export function mock(): void {
  // eslint-disable-next-line global-require, import/no-restricted-paths
  const MockAbsoluteDropdownContainer = require('test/lib/MockAbsoluteDropdownContainer').default;
  AbsoluteDropdownContainer = MockAbsoluteDropdownContainer;
}

export function restore(): void {
  AbsoluteDropdownContainer = _AbsoluteDropdownContainer;
}
